import React from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useClientId } from '../../../../../_hooks/useClientId';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { toast } from '../../../../../CommonComponents/OPL/Toast/Toast';
import {
  isConsumerArea as isConsumerAreaSelector,
  selectHasMobileNavigation,
} from '../../../../../CommonSelectors/CommonAreaSelector';
import { useBootstrapConsumerArea } from '../../../../../ConsumerArea/hooks/useBootstrapConsumerArea';
import {
  hasAddAnotherGoalSelector,
  hasTakeMeToPlanSelector,
} from '../../../../../MultiWorkflows/AdvWorkflow/AdvWorkflowSelector';
import { ACCOUNTS_QUERY_KEY } from '../../../../../Profile/Accounts/AccountsConstants';
import { ACCOUNT_PREFERENCES_QUERY_KEY } from '../../../../../Profile/Accounts/api/useAccountsQuery';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { useCharitableGivingMutation } from '../../api/useCharitableGivingMutation';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { useNavigation } from '../../hooks/useNavigation';
import { CharityNameReview } from '../CharityName/CharityName.review';
import { DafDonationYearReview } from '../DafDonationYear/DafDonationYear.review';
import { DafGrantAmountReview } from '../DafGrantAmount/DafGrantAmount.review';
import { DafGrantYearReview } from '../DafGrantYear/DafGrantYear.review';
import { DonationAmountReview } from '../DonationAmount/DonationAmount.review';
import { DonationYearReview } from '../DonationYear/DonationYear.review';
import { DonorReview } from '../Donor/Donor.review';
import { FundAccountsReview } from '../FundAccounts/FundAccounts.review';
import { KindOfPropertyReview } from '../KindOfProperty/KindOfProperty.review';
import { REVIEW_FIELDS_DICT, ReviewStep, STEP_ID } from './Review.meta';

const COMPONENT_DICT: Record<ReviewStep, React.FC> = {
  donor: DonorReview,
  kindOfProperty: KindOfPropertyReview,
  donationAmount: DonationAmountReview,
  donationYear: DonationYearReview,
  dafDonationYear: DafDonationYearReview,
  dafGrantYear: DafGrantYearReview,
  dafGrantAmount: DafGrantAmountReview,
  charityName: CharityNameReview,
  fundAccounts: FundAccountsReview,
};

export const Review = () => {
  const isConsumerArea = useSelector(isConsumerAreaSelector);
  const canAddAnotherGoal = useSelector(
    state =>
      !isConsumerArea || (hasAddAnotherGoalSelector(state) && !selectHasMobileNavigation(state)),
  );

  const {
    mutateAsync: bootstrapConsumerArea,
    isLoading: isBootstrappingConsumerArea,
  } = useBootstrapConsumerArea();

  const queryClient = useQueryClient();
  const { workflow, setStep } = useCharitableGivingContext();

  const {
    mutateAsync: saveWorkflow,
    isLoading: isSavingWorkflow,
    error: saveWorkflowError,
  } = useCharitableGivingMutation();

  const clientId = useClientId();

  const { goalId } = useRouteParams();

  const { progress, isFirstStep, previousStep } = useNavigation({
    currentStep: STEP_ID,
  });

  const onSubmit = async evt => {
    evt.preventDefault();

    try {
      const response = await saveWorkflow({ clientId, model: workflow.data });

      queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_PREFERENCES_QUERY_KEY, clientId]);

      toast.info('Goal was saved successfully.');

      const routeGoalId = goalId || response.Goals?.[response.Goals.length - 1].Id;

      // Follow the same pattern as other workflows (AdvWorkflowSummaryContainer.jsx line 44-53)
      const hasTakeMeToPlan = hasTakeMeToPlanSelector();

      if (isConsumerArea) {
        if (hasTakeMeToPlan) {
          ADV.util.Router.go2('ClientEditPlan', {
            clientId,
          });
        } else {
          const { componentName } = await bootstrapConsumerArea({
            clientId: `${clientId}`,
            completedWorkflow: true,
          });

          ADV.util.Router.go2(componentName || 'WorkflowDoneV2', {
            clientId,
          });
        }
      } else {
        ADV.util.Router.go2('Goal', {
          clientId,
          goalId: routeGoalId,
        });
      }
    } catch {
      // Errors are displayed in the alert
    }
  };

  if (workflow.data.givingStrategy === null) {
    throw new Error('[Review Step] Giving Strategy is not defined');
  }

  const reviewFields = REVIEW_FIELDS_DICT[workflow.data.givingStrategy];

  return (
    <StepContainer
      title="Please review the information below"
      onSubmit={onSubmit}
      alert={saveWorkflowError?.message}
      footer={
        <StepFooter
          isLoading={isSavingWorkflow || isBootstrappingConsumerArea}
          isFirstStep={isFirstStep}
          progress={progress}
          onPrevious={previousStep ? () => setStep(previousStep) : undefined}
          canAddAnotherGoal={canAddAnotherGoal}
        />
      }
    >
      <Box as="ul" display="flex" flexDirection="column" mx={-32} mb={-32} css="padding: 0">
        {reviewFields.map(step => {
          const Component = COMPONENT_DICT[step];

          if (Component) {
            return <Component key={step} />;
          }

          throw new Error(`No review component for step ${step}`);
        })}
      </Box>
    </StepContainer>
  );
};
